// ── Collateral Analytics ──────────────────────────────────────────────────
//
// Six views of the portfolio collateral record for one borrower, over time.
// The numbers come from dbo.CollateralHeaders / dbo.CollateralLines — the
// tables behind the Collateral tab on a loan — NOT from the collateral app's
// borrowing bases.
//
// THE GRAIN IS (as-of date, LOAN, collateral type). That is the whole design.
//
//   One account can carry four loans, each with different collateral: loans 1
//   and 2 A/R only, loan 3 A/R and M&E, loan 4 A/R, M&E and inventory. They
//   are reported on different days.
//
//   So carry-forward runs per LOAN per type, and aggregation happens after.
//   Doing it the other way round — summing to "A/R" first — makes A/R look
//   reported on any day ANY loan filed, so nothing carries and every one of
//   those days silently understates by the other loans' balances.
//
//   Multiple lines of the same type on the same loan and date are summed:
//   three A/R lines on loan 4 is one A/R position. Three loans each with A/R
//   is three positions, and they can be shown apart.
//
// Value Type (gross / eligible / availability) reuses calcCollateralLine from
// shared/collateral.js — the same pure function the collateral spreadsheet
// uses, so these charts agree with it by construction.

import { useState, useEffect, useMemo } from "react";
import { B, RAMP_GOLD, RAMP_CATEGORICAL, rampAt, INK_ON_LIGHT, HAIRLINE, HAIRLINE_FIRM, INK_MUTED, INK_PRIMARY, SURFACE } from "../../../shared/theme";
import { Card, Spinner, ErrorMsg, TabBtn, FilterSelect, Btn, Th, Td, TdNum } from "../../../shared/ui";
import { fmt, fmtDate, fmtRatio, exportCSV } from "../../../shared/format";
import { calcCollateralLine } from "../../../shared/collateral";
import { useApp } from "../../../shared/appContext";

const VIEWS = [
  { id: "gross",  label: "Gross Collateral" },
  { id: "ar$",    label: "A/R Aging ($)" },
  { id: "ar%",    label: "A/R Aging (%)" },
  { id: "inv$",   label: "Inventory Breakout ($)" },
  { id: "inv%",   label: "Inventory Breakout (%)" },
  { id: "ap$",    label: "A/P Aging ($)" },
];

// Palettes come from shared/theme. Aging is sequential — dark is the bad part,
// readable without consulting the legend. Collateral types and per-loan splits
// are categories, not a scale, so they take the categorical set.
const AGE_RAMP   = RAMP_GOLD;
const TYPE_RAMP  = RAMP_CATEGORICAL;
const SPLIT_RAMP = RAMP_CATEGORICAL;

const AR_BUCKETS = [
  { key: "araging0to30",   label: "0–30"   },
  { key: "araging31to60",  label: "31–60"  },
  { key: "araging61to90",  label: "61–90"  },
  { key: "araging90plus",  label: "91–120" },
  { key: "araging120plus", label: "120+"   },
];
const INV_BUCKETS = [
  { key: "invgeneral",        label: "General"        },
  { key: "invrawmaterials",   label: "Raw Materials"  },
  { key: "invworkinprogress", label: "WIP"            },
  { key: "invfinishedgoods",  label: "Finished Goods" },
  { key: "invother",          label: "Other"          },
];
const AP_BUCKETS = [
  { key: "apaging0to30",  label: "0–30"  },
  { key: "apaging31to60", label: "31–60" },
  { key: "apaging61to90", label: "61–90" },
  { key: "apaging90plus", label: "90+"   },
];

// A loan is live if it is still accruing or on non-accrual. Everything else —
// Paid Off, Charged Off, Terminated, Funding in Progress — is resolved: its
// collateral was released, written off, or never advanced against. Carrying a
// paid-off loan's collateral forward for two years after payoff is the "looks
// silly" case, and it inflates every total on the page.
//
// loanstatus is NOT a column. getLoans derives it from legal events, funded
// terms and terminations in a thirty-line CASE. Reimplementing that here is how
// two definitions of "paid off" end up disagreeing, so this page asks getLoans
// and uses its answer.
const LIVE_STATUSES = ["Accrual", "Non Accrual"];

const num = v => parseFloat(v || 0) || 0;

// One decimal, and only when it earns its place: 10,000,000 reads "10M", not
// "10.0M". A chart label is a glance, not a reconciliation — the exact figure
// is in the table underneath and in the hover.
const trim1 = x => String(Math.abs(x) >= 100 ? Math.round(x) : Math.round(x * 10) / 10);
const short = v => {
  const a = Math.abs(v);
  return a >= 1e9 ? trim1(v / 1e9) + "B"
       : a >= 1e6 ? trim1(v / 1e6) + "M"
       : a >= 1e3 ? trim1(v / 1e3) + "K"
       : String(Math.round(v));
};

// Label ink rides with the fill. Every series below carries the ink that reads
// on it, chosen in shared/theme where the palette is defined — a fill here is
// the string "var(--brand-cyan, #12b5db)", so there is no colour to measure at
// runtime even if measuring were the right place to decide it.

// ── stacked bar chart with an optional total line ──────────────────────────
const StackedChart = ({ periods, series, pct, lineLabel, anyCarried,
                        allSeries, hidden, onToggle, onIsolate, onShowAll }) => {
  // PADT leaves room for the total sitting above each bar.
  const H = 274, PADL = 54, PADB = 58, PADT = 26;
  const n = periods.length;
  if (!n) return <div style={{ padding:"40px 0", textAlign:"center", color:INK_MUTED, fontSize:13 }}>No periods in range.</div>;

  // Wider than it was: a bar has to hold "10.5M" without the neighbours
  // touching it. The chart scrolls horizontally, so length is cheap.
  const W = Math.max(640, PADL + n * 46 + 24);
  const totals = periods.map(p => series.reduce((s, sr) => s + num(p.vals[sr.key]), 0));
  const max = pct ? 1 : Math.max(...totals, 1);
  const step = (W - PADL - 24) / n;
  const bw = Math.min(34, step * 0.68);
  const y = v => PADT + (H - PADT - PADB) * (1 - (pct ? v : v / max));

  // Suppress rather than overlap. Below these thresholds a label is noise.
  const showTotals = step >= 30;
  const fitsInBar = h => h >= 13 && bw >= 22;

  return (
    <div style={{ overflowX:"auto" }}>
      <svg width={W} height={H} role="img" style={{ display:"block" }}>
        {[0, 0.5, 1].map(g => (
          <g key={g}>
            <line x1={PADL} x2={W - 12} y1={y(pct ? g : g * max)} y2={y(pct ? g : g * max)}
                  stroke={HAIRLINE} strokeDasharray="2 3" />
            <text x={PADL - 8} y={y(pct ? g : g * max) + 4} textAnchor="end"
                  style={{ fontSize:10, fill:INK_MUTED }}>
              {pct ? Math.round(g * 100) + "%" : short(g * max)}
            </text>
          </g>
        ))}

        {periods.map((p, i) => {
          const tot = totals[i] || 0;
          const x = PADL + i * step + (step - bw) / 2;
          let acc = 0;
          return (
            <g key={p.key}>
              {series.map(sr => {
                const raw = num(p.vals[sr.key]);
                const v = pct ? (tot ? raw / tot : 0) : raw;
                if (v <= 0) return null;
                const h = (H - PADT - PADB) * (pct ? v : v / max);
                acc += v;
                // Carried figures are washed out with a dashed edge. They keep
                // their real height — the shape of the book is the point — but
                // cannot be mistaken for something filed.
                const c = (p.carried || {})[sr.key];
                const top = y(acc);
                return (
                  <g key={sr.key}>
                    <rect x={x} width={bw} y={top} height={Math.max(h, 0.5)}
                          fill={sr.color} fillOpacity={c ? 0.3 : 1}
                          stroke={c ? sr.color : "none"}
                          strokeDasharray={c ? "3 2" : undefined} strokeWidth={c ? 1 : 0}>
                      <title>{`${p.label} · ${sr.label}: ${pct ? fmtRatio(v) : fmt(raw)}`
                        + (c ? `\n${c.n < c.of ? `${c.n} of ${c.of} loans carried` : "carried"} forward from ${fmtDate(c.from)}` : "")}</title>
                    </rect>
                    {/* Segment value, in the segment. Only where it fits —
                        a number spilling over two bands is worse than none.
                        pointerEvents off so the hover still hits the bar. */}
                    {fitsInBar(h) && (
                      <text x={x + bw / 2} y={top + h / 2 + 3} textAnchor="middle"
                            style={{ fontSize:9, fontWeight:600, pointerEvents:"none",
                                     // A carried segment sits at 30% opacity,
                                     // so it is pale whatever its fill is.
                                     fill: c ? INK_ON_LIGHT : sr.ink }}>
                        {pct ? Math.round(v * 100) + "%" : short(raw)}
                      </text>
                    )}
                  </g>
                );
              })}
              {/* Total above the stack. Not on the percent views — it is 100%
                  on every bar there, which tells the reader nothing. */}
              {!pct && showTotals && tot > 0 && (
                // Haloed: the total line passes through exactly this band, and
                // a number with a line struck through it is worse than no
                // number. paint-order puts the stroke behind the glyph.
                <text x={x + bw / 2} y={Math.max(y(tot) - 7, 11)} textAnchor="middle"
                      style={{ fontSize:10, fontWeight:700, fill:INK_PRIMARY,
                               paintOrder:"stroke", stroke:SURFACE,
                               strokeWidth:3, strokeLinejoin:"round", pointerEvents:"none" }}>
                  {short(tot)}
                </text>
              )}
              <text x={x + bw / 2} y={H - PADB + 14} textAnchor="end"
                    transform={`rotate(-55 ${x + bw / 2} ${H - PADB + 14})`}
                    style={{ fontSize:9, fill:INK_MUTED }}>{p.label}</text>
            </g>
          );
        })}

        {!pct && (
          <polyline fill="none" stroke={B.cyan} strokeWidth="2"
            points={periods.map((p, i) => `${PADL + i * step + step / 2},${y(totals[i])}`).join(" ")} />
        )}
      </svg>

      {/* ── legend, and the filter ────────────────────────────────────────
          The legend IS the control. Click a bucket to drop it out of the
          chart, the total and the table; double-click (or shift-click) to
          keep only that one. A legend you can only read is a wasted row of
          pixels when the question is usually "what does this look like
          without the 120+ tail". */}
      <div style={{ display:"flex", flexWrap:"wrap", gap:8, justifyContent:"center", marginTop:10, fontSize:11 }}>
        {(allSeries || series).map(sr => {
          const off = !!(hidden && hidden.has(sr.key));
          return (
            <button key={sr.key} type="button"
                    onClick={e => (e.shiftKey || e.altKey) ? onIsolate(sr.key) : onToggle(sr.key)}
                    onDoubleClick={() => onIsolate(sr.key)}
                    title={off ? `${sr.label} — hidden. Click to bring it back.`
                               : `${sr.label} — click to remove it from the chart, total and table. Double-click to show only this one.`}
                    style={{ display:"inline-flex", alignItems:"center", gap:6, cursor:"pointer",
                             padding:"3px 9px", borderRadius:20, fontSize:11, lineHeight:1.4,
                             fontFamily:"inherit",
                             border:"1px solid " + (off ? HAIRLINE : HAIRLINE_FIRM),
                             background: off ? "transparent" : SURFACE,
                             color: off ? INK_MUTED : INK_PRIMARY,
                             opacity: off ? 0.5 : 1,
                             textDecoration: off ? "line-through" : "none" }}>
              <span style={{ width:9, height:9, borderRadius:"50%", display:"inline-block",
                             background: off ? "transparent" : sr.color,
                             border:"1px solid " + sr.color }} />
              {sr.label}
            </button>
          );
        })}
        {hidden && hidden.size > 0 && (
          <button type="button" onClick={onShowAll}
                  title="Bring every bucket back."
                  style={{ padding:"3px 9px", borderRadius:20, fontSize:11, lineHeight:1.4,
                           fontFamily:"inherit", cursor:"pointer", fontWeight:600,
                           border:"1px solid "+B.amber, background:B.amberLight, color:B.amberDark }}>
            Show all ({hidden.size} hidden)
          </button>
        )}
      </div>

      <div style={{ display:"flex", flexWrap:"wrap", gap:14, justifyContent:"center", marginTop:8, fontSize:11 }}>
        {!pct && lineLabel && (
          <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}>
            <span style={{ width:16, height:2, background:B.cyan, display:"inline-block" }} />
            {lineLabel}
          </span>
        )}
        {anyCarried > 0 && (
          <span style={{ display:"inline-flex", alignItems:"center", gap:5, color:INK_MUTED }}>
            <span style={{ width:9, height:9, borderRadius:2, background:B.black40, opacity:0.3,
                           border:"1px dashed "+B.black40, display:"inline-block" }} />
            carried forward
          </span>
        )}
      </div>
    </div>
  );
};

export const CollateralAnalyticsPage = () => {
  const { api } = useApp();

  const [accounts, setAccounts] = useState([]);
  const [accountid, setAccountid] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState({ headers: [], lines: [] });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [view, setView] = useState("gross");
  const [valueType, setValueType] = useState("availability");
  const [typeFilter, setTypeFilter] = useState("");
  const [loanFilter, setLoanFilter] = useState("");
  // { [loannumber]: { status, facilitytype } } — both come from the same
  // getLoans call, so the picker costs no extra round trip.
  const [loanMeta, setLoanMeta] = useState({});
  // Default ON: the page is normally asked "where does this credit stand", and
  // a resolved loan's collateral is not part of that answer.
  const [hideResolved, setHideResolved] = useState(true);
  const [splitByLoan, setSplitByLoan] = useState(false);
  // Default ON: collateral types are filed on different days, and per loan.
  // Without this the chart collapses to zero on every date some other loan or
  // type happened to be the one filed. Carrying is an assumption, so carried
  // figures are always marked rather than blended in silently.
  const [carry, setCarry] = useState(true);
  // Buckets dropped from the chart by clicking the legend. Kept PER VIEW —
  // hiding the 120+ tail on A/R has nothing to say about which inventory
  // classes you want to see, and losing your selection every time you check
  // another tab is the kind of small friction that makes a tool annoying.
  const [hiddenByView, setHiddenByView] = useState({});

  useEffect(() => {
    api.get("getAccounts").then(d => setAccounts(Array.isArray(d) ? d : [])).catch(() => setAccounts([]));
  }, [api]);

  useEffect(() => {
    if (!accountid) { setData({ headers: [], lines: [] }); return; }
    setLoading(true); setErr(null);
    const qs = ["accountid=" + encodeURIComponent(accountid)];
    if (from) qs.push("from=" + from);
    if (to)   qs.push("to=" + to);
    api.get("getCollateralAnalytics?" + qs.join("&"))
      .then(d => { setData({ headers: d?.headers || [], lines: d?.lines || [] }); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  }, [api, accountid, from, to]);

  useEffect(() => {
    if (!accountid) { setLoanMeta({}); return; }
    api.get("getLoans?accountid=" + encodeURIComponent(accountid))
      .then(d => {
        // Keyed by loan NUMBER to match the grain above. If two rows share a
        // number, a live status wins — never hide a loan that is still
        // accruing because a sibling record says otherwise. facilitytype is
        // taken from whichever row supplies one.
        const m = {};
        (Array.isArray(d) ? d : []).forEach(l => {
          const k = String(l.loannumber ?? l.id);
          const prev = m[k];
          const takeStatus = !prev || LIVE_STATUSES.includes(l.loanstatus);
          m[k] = {
            status: takeStatus ? l.loanstatus : prev.status,
            facilitytype: (prev && prev.facilitytype) || l.facilitytype || null,
          };
        });
        setLoanMeta(m);
      })
      .catch(() => setLoanMeta({}));
  }, [api, accountid]);

  // Changing account invalidates any loan selection from the previous one.
  useEffect(() => { setLoanFilter(""); setTypeFilter(""); }, [accountid]);

  // ── periods, at the true grain: date -> loan -> type/bucket ───────────────
  const periods = useMemo(() => {
    const by = {};
    const keyOf = d => String(d).slice(0, 10);
    // Grouped on LOAN NUMBER, not the internal id. Loan 500027 is one loan to
    // everybody who uses this system; if its collateral was filed under more
    // than one internal id it is still one loan, and splitting it would both
    // duplicate the picker and carry the halves independently.
    const slot = (k, date, loanid, loannumber) => {
      const lk = loannumber ? String(loannumber) : String(loanid);
      // `label` is what the x-axis prints and what the bar tooltip leads with.
      // It was missing: every period object carried key/date but no label, so
      // both rendered the string "undefined". The rows the chart actually
      // receives are spreads of these objects ({ ...p, vals, rep }), so adding
      // it here is what reaches the chart.
      const e = by[k] = by[k] || { key:k, date, label: fmtDate(date), byLoan:{} };
      const L = e.byLoan[lk] = e.byLoan[lk] ||
        { loanid: lk, loannumber: loannumber || String(loanid), ids: new Set(),
          types:{}, vals:{}, hasAP:false, hasAR:false, hasINV:false };
      L.ids.add(String(loanid));
      return L;
    };

    (data.headers || []).forEach(h => {
      const L = slot(keyOf(h.asofdate), h.asofdate, h.loanid, h.loannumber);
      AP_BUCKETS.forEach(b => { L.vals[b.key] = num(L.vals[b.key]) + num(h[b.key]); });
      L.hasAP = true;
    });

    (data.lines || []).forEach(l => {
      // hdr_loanid, not l.loanid: CollateralLines denormalizes loanid and the
      // header's is authoritative. See the alias in getCollateralAnalytics.
      const L = slot(keyOf(l.asofdate), l.asofdate, l.hdr_loanid ?? l.loanid, l.loannumber);
      [...AR_BUCKETS, ...INV_BUCKETS].forEach(b => { L.vals[b.key] = num(L.vals[b.key]) + num(l[b.key]); });

      // NULL is the only thing separating "not reported" from "reported zero",
      // so test nullness before num() flattens it.
      const filed = ks => ks.some(b => l[b.key] !== null && l[b.key] !== undefined);
      if (filed(AR_BUCKETS))  L.hasAR  = true;
      if (filed(INV_BUCKETS)) L.hasINV = true;

      // Same type, same loan, same date = one position.
      const c = calcCollateralLine(l);
      const t = l.collateraltype || "Unspecified";
      const s = L.types[t] = L.types[t] || { gross:0, eligible:0, availability:0 };
      s.gross += num(l.grossamount);
      s.eligible += c.eligible;
      s.availability += c.collateralvalue;
    });

    return Object.values(by).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [data]);

  const allLoans = useMemo(() => {
    const m = {};
    periods.forEach(p => Object.values(p.byLoan).forEach(L => { m[L.loanid] = L.loannumber || String(L.loanid); }));
    return Object.entries(m)
      .map(([loanid, loannumber]) => {
        const meta = loanMeta[String(loannumber)] || loanMeta[String(loanid)] || {};
        const status = meta.status || null;
        // Unknown status is treated as LIVE. A loan the status lookup failed to
        // return should not silently vanish from a credit review — showing an
        // extra loan is a smaller error than hiding one.
        return { loanid, loannumber, status, facilitytype: meta.facilitytype || null,
                 live: !status || LIVE_STATUSES.includes(status) };
      })
      .sort((a, b) => String(a.loannumber).localeCompare(String(b.loannumber)));
  }, [periods, loanMeta]);

  const resolvedCount = allLoans.filter(L => !L.live).length;

  // Which of the optional views this borrower has anything to say about.
  //
  // Deliberately measured across EVERY loan on the account, not the filtered
  // set: the tab strip should describe what the borrower reports, and stay put
  // while you move the loan and active-only filters. Tabs that appear and
  // vanish as you toggle a checkbox — and can pull the view out from under you
  // mid-read — are worse than one empty chart.
  //
  // Substance, not presence: an A/P header exists for every certificate, and a
  // borrower who files Inventory as a collateral type but never breaks it into
  // raw/WIP/finished has nothing to show on the breakout.
  const available = useMemo(() => {
    let ar = false, inv = false, ap = false;
    periods.forEach(p => Object.values(p.byLoan).forEach(L => {
      if (!ar)  ar  = AR_BUCKETS.some(b => num(L.vals[b.key]) > 0);
      if (!inv) inv = INV_BUCKETS.some(b => num(L.vals[b.key]) > 0);
      if (!ap)  ap  = AP_BUCKETS.some(b => num(L.vals[b.key]) > 0);
    }));
    return { ar, inv, ap };
  }, [periods]);

  // Before a company is chosen there is nothing to judge by, so show the full
  // strip rather than a lone tab that implies the page only does one thing.
  const views = useMemo(() => (
    periods.length === 0 ? VIEWS : VIEWS.filter(v =>
      v.id === "gross" ? true
      : v.id.startsWith("ar")  ? available.ar
      : v.id.startsWith("inv") ? available.inv
      : available.ap)
  ), [periods, available]);

  // If the tab you were on is gone — switching to a borrower with no inventory,
  // say — fall back rather than rendering a view that no longer exists.
  useEffect(() => {
    if (!views.some(v => v.id === view)) setView("gross");
  }, [views, view]);

  const allTypes = useMemo(() => {
    const s = new Set();
    periods.forEach(p => Object.values(p.byLoan).forEach(L => Object.keys(L.types).forEach(t => s.add(t))));
    return [...s].sort();
  }, [periods]);

  const spec = useMemo(() => {
    const pct = view.endsWith("%");
    const isGross = view === "gross";
    const kind = isGross ? "GROSS" : view.startsWith("ar") ? "AR" : view.startsWith("inv") ? "INV" : "AP";
    const buckets = kind === "AR" ? AR_BUCKETS : kind === "INV" ? INV_BUCKETS : kind === "AP" ? AP_BUCKETS : [];
    const labelOf = k => isGross ? k : (buckets.find(b => b.key === k) || {}).label || k;

    // A/R aging is never carried. A balance is one thing — it sits there until
    // someone tells you otherwise, so last-known is a defensible stand-in. An
    // aging DISTRIBUTION is a snapshot of a moving queue: reprinting last
    // month's buckets on this month's date asserts that nothing rolled from
    // 0–30 into 31–60, which is the one thing you can be sure did happen.
    // Report what was filed and leave the rest blank.
    const carriable = kind !== "AR";

    // Explicitly picking a loan overrides the hide — if you asked for the
    // paid-off one by name, you want to see it.
    const loans = allLoans.filter(L =>
      loanFilter ? String(L.loanid) === String(loanFilter)
                 : (!hideResolved || L.live));
    const baseKeys = isGross ? (typeFilter ? [typeFilter] : allTypes) : buckets.map(b => b.key);

    // ── 1. values and "was it filed" per LOAN per key ────────────────────
    const unitRows = periods.map(p => {
      const vals = {}, rep = {};
      loans.forEach(L => {
        const src = p.byLoan[L.loanid];
        // Aging and inventory carry as a SET — half a carried distribution
        // and half a live one is a shape that never existed.
        const setSum = src ? buckets.reduce((t, b) => t + num(src.vals[b.key]), 0) : 0;
        const setFiled = !!(src && (kind === "AR" ? src.hasAR : kind === "INV" ? src.hasINV : src.hasAP) && setSum > 0);
        baseKeys.forEach(bk => {
          const uk = L.loanid + "|" + bk;
          if (isGross) {
            const g = src && src.types[bk];
            vals[uk] = g ? g[valueType] : 0;
            // Tested on GROSS, not on the selected value type: a position
            // filed at $5mm that nets to zero availability after reserves is
            // a real zero and must not be papered over.
            rep[uk] = !!(g && num(g.gross) > 0);
          } else {
            vals[uk] = src ? num(src.vals[bk]) : 0;
            rep[uk] = setFiled;
          }
        });
      });
      return { ...p, vals, rep };
    });

    // The last date each loan filed anything at all. For a RESOLVED loan this
    // is where its collateral story ends — after payoff there is nothing left
    // to carry, and carrying anyway is what made the chart look silly.
    const lastFiled = {}, firstFiled = {};
    unitRows.forEach(r => {
      Object.keys(r.rep).forEach(uk => {
        if (!r.rep[uk]) return;
        const lid = uk.slice(0, uk.indexOf("|"));
        if (!lastFiled[lid]  || new Date(r.date) > new Date(lastFiled[lid]))  lastFiled[lid]  = r.date;
        if (!firstFiled[lid] || new Date(r.date) < new Date(firstFiled[lid])) firstFiled[lid] = r.date;
      });
    });
    const liveById = {};
    allLoans.forEach(L => { liveById[String(L.loanid)] = L.live; });

    // ── 1b. non-carriable views: drop the dates nobody filed ────────────────
    // Without carry, a date whose header held only M&E would draw a column of
    // zero A/R. That is not "no receivables" — it is "not asked". Only dates
    // with an actual A/R filing belong on the axis. Where SOME loans filed and
    // others did not, the date stays but the row is marked, because the
    // aggregate is understating by whatever the silent loans hold.
    let filedRows = unitRows;
    if (!carriable) {
      filedRows = unitRows.map(r => {
        let n = 0, of = 0;
        loans.forEach(L => {
          const lid = String(L.loanid);
          if (Object.keys(r.rep).some(uk => uk.slice(0, uk.indexOf("|")) === lid && r.rep[uk])) { n++; of++; return; }
          // Expected only while the loan is within its own reporting life.
          if (!firstFiled[lid]) return;
          if (new Date(r.date) < new Date(firstFiled[lid])) return;
          if (!liveById[lid] && new Date(r.date) > new Date(lastFiled[lid])) return;
          of++;
        });
        return { ...r, filedLoans: n, expectedLoans: of };
      }).filter(r => r.filedLoans > 0);
    }

    // ── 2. carry forward, per loan per key ──────────────────────────────
    // Nothing is carried backward: a loan shows blank until its first filing
    // rather than inventing history it never had. And nothing is carried past
    // a resolved loan's last filing: its collateral was released.
    let carriedRows = filedRows;
    if (carry && carriable) {
      const last = {};
      carriedRows = filedRows.map(r => {
        const vals = { ...r.vals }, carried = {};
        Object.keys(r.rep).forEach(uk => {
          const lid = uk.slice(0, uk.indexOf("|"));
          if (r.rep[uk]) { last[uk] = { v: num(vals[uk]), d: r.date }; return; }
          if (!last[uk]) return;
          const ended = !liveById[lid] && lastFiled[lid] && new Date(r.date) > new Date(lastFiled[lid]);
          if (ended) { vals[uk] = 0; return; }
          vals[uk] = last[uk].v;
          carried[uk] = last[uk].d;
        });
        return { ...r, vals, carried };
      });
    }

    // ── 3. project onto display series ──────────────────────────────────
    const hiddenSet = new Set(hiddenByView[view] || []);
    let series, rows;
    if (splitByLoan && loans.length > 1) {
      series = [];
      loans.forEach(L => baseKeys.forEach(bk => {
        const c = rampAt(SPLIT_RAMP, series.length);
        series.push({ key: L.loanid + "|" + bk,
                      label: L.loannumber + " · " + labelOf(bk),
                      color: c.fill, ink: c.ink });
      }));
      rows = carriedRows.map(r => {
        const carried = {};
        Object.entries(r.carried || {}).forEach(([uk, d]) => { carried[uk] = { from: d, n: 1, of: 1 }; });
        return { ...r, carried };
      });
    } else {
      series = baseKeys.map((bk, i) => {
        const c = rampAt(isGross ? TYPE_RAMP : AGE_RAMP, i);
        return { key: bk, label: labelOf(bk), color: c.fill, ink: c.ink };
      });
      rows = carriedRows.map(r => {
        const vals = {}, carried = {};
        baseKeys.forEach(bk => {
          let sum = 0, fromDate = null, nCarried = 0, nContrib = 0;
          loans.forEach(L => {
            const uk = L.loanid + "|" + bk;
            if (!(uk in r.vals)) return;
            const v = num(r.vals[uk]);
            const c = (r.carried || {})[uk];
            if (v !== 0 || c) nContrib++;
            sum += v;
            if (c) { nCarried++; if (!fromDate || new Date(c) > new Date(fromDate)) fromDate = c; }
          });
          vals[bk] = sum;
          // A combined figure part-carried is still part-carried. Saying so is
          // the difference between a number a credit officer can rely on and
          // one they cannot.
          if (nCarried) carried[bk] = { from: fromDate, n: nCarried, of: nContrib };
        });
        return { ...r, vals, carried };
      });
    }

    // The legend keeps every series so a hidden one can be clicked back on;
    // everything downstream — chart, total line, table columns, Total column,
    // percent denominators, CSV — reads the visible set only. On a percent
    // view that means the remaining buckets renormalize to 100%, which is the
    // only thing a 100%-stacked bar can honestly do. The header says so.
    const allSeries = series;
    const visible = series.filter(s => !hiddenSet.has(s.key));
    series = visible.length ? visible : allSeries;
    const hiddenCountSeries = allSeries.length - series.length;
    // Only keys that actually exist in THIS view's series count as hidden —
    // switching Separate by loan on and off changes the key shape, and a
    // stale key must not show up as a phantom in the "n hidden" count.
    const activeHidden = new Set(hiddenCountSeries > 0
      ? allSeries.filter(s => hiddenSet.has(s.key)).map(s => s.key) : []);

    const carriedCount = rows.reduce((t, r) => t + Object.keys(r.carried || {}).length, 0);
    const title = isGross ? "Collateral"
                : kind === "AR" ? "Accounts Receivable Aging"
                : kind === "INV" ? "Inventory Breakout" : "Accounts Payable Aging";
    const hiddenCount = loanFilter ? 0 : allLoans.length - loans.length;
    const skipped = carriable ? 0 : periods.length - rows.length;
    const note = [isGross ? "Value type: " + valueType : null,
                  loanFilter ? "1 loan" : (loans.length > 1 ? loans.length + " loans" : null),
                  hiddenCount > 0 ? hiddenCount + " resolved hidden" : null,
                  !carriable ? "filed periods only" : null,
                  skipped > 0 ? skipped + " with no A/R filing omitted" : null,
                  hiddenCountSeries > 0
                    ? `${series.length} of ${allSeries.length} shown${pct ? ", % of shown" : ""}`
                    : null,
                  ].filter(Boolean).join(" · ") || null;

    return { series, allSeries, hiddenSet: activeHidden, rows, pct,
             lineLabel: isGross ? "Collateral Amt" : title,
             title, note, carriedCount, loans, carriable,
             canSplit: isGross && !loanFilter && loans.length > 1 };
  }, [view, periods, allLoans, allTypes, typeFilter, valueType, carry, loanFilter,
      splitByLoan, hideResolved, hiddenByView]);

  // Toggling the last visible bucket off would leave an empty chart and no
  // obvious way back, so that click is a no-op. Isolate is the way to get to
  // one bucket, and clicking the isolated one again restores the set.
  const toggleSeries = key => setHiddenByView(prev => {
    const cur = new Set(prev[view] || []);
    if (cur.has(key)) cur.delete(key);
    else {
      if (spec.allSeries.filter(s => !cur.has(s.key)).length <= 1) return prev;
      cur.add(key);
    }
    return { ...prev, [view]: [...cur] };
  });
  const isolateSeries = key => setHiddenByView(prev => {
    const others = spec.allSeries.map(s => s.key).filter(k => k !== key);
    const cur = new Set(prev[view] || []);
    // "Already isolated" means THIS key is the one left standing — not merely
    // that the same NUMBER of buckets is hidden. Counting instead of checking
    // makes isolating a second bucket read as un-isolating the first, and the
    // chart springs back to all five.
    const alreadyIsolated = !cur.has(key) && others.every(k => cur.has(k));
    return { ...prev, [view]: alreadyIsolated ? [] : others };
  });
  const showAllSeries = () => setHiddenByView(prev => ({ ...prev, [view]: [] }));

  const rowTotal = r => spec.series.reduce((s, sr) => s + num(r.vals[sr.key]), 0);
  const latest = spec.rows.length ? spec.rows[spec.rows.length - 1] : null;

  const exportRows = () => spec.rows.map(r => {
    const t = rowTotal(r);
    const o = { as_of: fmtDate(r.date) };
    spec.series.forEach(sr => {
      o[sr.label] = num(r.vals[sr.key]).toFixed(2);
      if (spec.pct) o[sr.label + " %"] = t ? ((num(r.vals[sr.key]) / t) * 100).toFixed(1) : "0.0";
    });
    o.Total = t.toFixed(2);
    return o;
  });

  const NEVER_FUNDED = ["Funding in Progress", "Terminated"];
  const accountOpts = accounts
    .filter(a => !NEVER_FUNDED.includes(a.accountstatus))
    .sort((a, b) => (a.companyname || "").localeCompare(b.companyname || ""))
    .map(a => ({ value: a.id, label: a.companyname }));

  const dateInput = { padding:"6px 10px", borderRadius:8, fontSize:13,
                      border:"1px solid "+HAIRLINE_FIRM,
                      background:SURFACE };
  const checkLabel = { display:"flex", alignItems:"center", gap:6, fontSize:12, cursor:"pointer",
                       whiteSpace:"nowrap", color:INK_MUTED };

  return (
    <>
      <Card style={{ padding:14, marginBottom:14 }}>
        <div style={{ display:"flex", flexWrap:"wrap", gap:14, alignItems:"center" }}>
          <FilterSelect label="Company" value={accountid} onChange={setAccountid}
                        options={accountOpts} allLabel="— Select a company —" />
          {/* Every loan stays selectable by name even when resolved ones are
              hidden in aggregate — picking one explicitly is a deliberate act.
              The control is always rendered: one that vanishes reads as broken,
              one greyed out with a reason reads as "not yet", and the
              placeholder says which state you are in. */}
          <FilterSelect label="Loan" value={loanFilter} onChange={setLoanFilter}
                        options={allLoans.map(L => ({
                          value: L.loanid,
                          // number · facility type (status when resolved) — the
                          // facility is what tells you which loan you want; the
                          // number alone rarely does.
                          label: [L.loannumber, L.facilitytype].filter(Boolean).join("  ·  ")
                                 + (L.live ? "" : "  (" + (L.status || "resolved") + ")"),
                        }))}
                        allLabel={!accountid ? "Select a company first"
                                : loading ? "Loading…"
                                : allLoans.length === 0 ? "No loans"
                                : (hideResolved && resolvedCount > 0 ? "All active loans" : "All loans")}
                        disabled={!accountid || loading || allLoans.length === 0} />
          <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13 }}>
            <span style={{ fontSize:11, fontWeight:700, color:INK_MUTED,
                           textTransform:"uppercase", letterSpacing:"0.06em" }}>From</span>
            <input type="date" value={from} onChange={e=>setFrom(e.target.value)} style={dateInput} />
          </label>
          <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13 }}>
            <span style={{ fontSize:11, fontWeight:700, color:INK_MUTED,
                           textTransform:"uppercase", letterSpacing:"0.06em" }}>To</span>
            <input type="date" value={to} onChange={e=>setTo(e.target.value)} style={dateInput} />
          </label>
          {(from || to) && <Btn onClick={()=>{ setFrom(""); setTo(""); }}>Clear dates</Btn>}

          <div style={{ marginLeft:"auto", display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
            {view === "gross" && (
              <>
                <FilterSelect label="Value" value={valueType} onChange={setValueType}
                  options={[{value:"availability",label:"Availability"},
                            {value:"eligible",label:"Eligible"},
                            {value:"gross",label:"Gross"}]} allLabel={null} />
                <FilterSelect label="Type" value={typeFilter} onChange={setTypeFilter}
                  options={allTypes} allLabel="All types" />
              </>
            )}
            {spec.canSplit && (
              <label style={checkLabel}
                     title="Off, each collateral type is one bar across every loan. On, a type held on three loans shows as three series so you can see which loan it sits on.">
                <input type="checkbox" checked={splitByLoan} onChange={e=>setSplitByLoan(e.target.checked)} />
                Separate by loan
              </label>
            )}
            {/* Also always rendered, for the same reason — its absence was
                read as a missing feature rather than "nothing to hide". */}
            <label style={{ ...checkLabel,
                            opacity: (resolvedCount > 0 && !loanFilter) ? 1 : 0.45,
                            cursor: (resolvedCount > 0 && !loanFilter) ? "pointer" : "default" }}
                   title={!accountid ? "Select a company first."
                        : loanFilter ? "A single loan is selected, so there is nothing to filter."
                        : resolvedCount === 0 ? "Every loan on this account is active — nothing to hide."
                        : "Hides loans that are not Accrual or Non Accrual — paid off, charged off, terminated, or never funded. Their collateral was released, so carrying it forward inflates the totals. " + resolvedCount + " on this account."}>
              <input type="checkbox" checked={hideResolved}
                     disabled={resolvedCount === 0 || !!loanFilter}
                     onChange={e=>setHideResolved(e.target.checked)} />
              Active loans only{resolvedCount > 0 ? ` (${resolvedCount} hidden)` : ""}
            </label>
            {/* Greyed rather than removed on A/R, so the absence reads as a
                deliberate rule instead of a control that went missing. */}
            <label style={{ ...checkLabel,
                            opacity: spec.carriable ? 1 : 0.45,
                            cursor: spec.carriable ? "pointer" : "default" }}
                   title={spec.carriable
                        ? "Collateral is filed per loan on different days. Off, an unreported loan/type reads as $0.00; on, its last filed balance carries and is marked. A resolved loan never carries past its final filing. A type filed at zero counts as not reported — the two are indistinguishable in the data."
                        : "A/R aging is never carried forward. An aging distribution is a snapshot of a moving queue — repeating last month's buckets would assert that nothing aged, so only filed periods are shown."}>
              <input type="checkbox" checked={spec.carriable && carry}
                     disabled={!spec.carriable}
                     onChange={e=>setCarry(e.target.checked)} />
              Carry forward unreported
            </label>
            <Btn onClick={()=>exportCSV(exportRows(),
                 ["as_of", ...spec.series.map(s=>s.label), "Total"],
                 "collateral-" + view.replace(/[^a-z]/gi,"") + ".csv")}
                 disabled={!spec.rows.length}>Export CSV</Btn>
          </div>
        </div>
      </Card>

      {/* ── provenance ──────────────────────────────────────────────────────
          Two apps in ACE both say "collateral" and they are not the same
          number. This page reads the Portfolio record — figures an analyst has
          reviewed and keyed. The Collateral app reports what the borrower
          submitted. They are allowed to disagree, and anyone reading a chart
          here needs to know which one they are holding. */}
      <div style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"10px 14px",
                    marginBottom:14, borderRadius:10, background:B.amberLight,
                    borderLeft:"3px solid "+B.amber, fontSize:12, lineHeight:1.5,
                    color:INK_PRIMARY }}>
        <span style={{ fontSize:14, lineHeight:1.2 }} aria-hidden="true">ⓘ</span>
        <span>
          <strong>Source: Portfolio application — reviewed and entered internally.</strong>{" "}
          These figures come from the collateral record maintained in the Portfolio app.
          They will not necessarily tie to the Collateral application, which reports
          borrower-provided certificate data as submitted.
        </span>
      </div>

      <div style={{ display:"flex", gap:3, flexWrap:"wrap", marginBottom:-1 }}>
        {views.map(v => <TabBtn key={v.id} id={v.id} current={view} label={v.label} onClick={setView} />)}
      </div>

      {!accountid
        ? <Card style={{ padding:40, textAlign:"center", color:INK_MUTED, fontSize:13 }}>
            Select a company to see its collateral history.
          </Card>
        : loading ? <Spinner />
        : err ? <ErrorMsg msg={err} onRetry={()=>setAccountid(accountid)} />
        : !periods.length
          ? <Card style={{ padding:40, textAlign:"center", color:INK_MUTED, fontSize:13 }}>
              No collateral records for this company{(from||to) ? " in that date range" : ""}.
            </Card>
          : <>
              {/* ── the cards ARE the bucket filter ─────────────────────────
                  Click one to drop that bucket out of the chart, the total
                  line, the table and the export; double-click (or shift-click)
                  to keep only it. A dropped card stays put, struck through, so
                  the way back is the thing you just clicked rather than a
                  reset button somewhere else. Cards render from allSeries, not
                  the visible set, for exactly that reason. */}
              {latest && spec.allSeries.length <= 10 && (
                <div style={{ display:"flex", flexWrap:"wrap", gap:12, margin:"14px 0" }}>
                  {spec.allSeries.map(sr => {
                    const off = spec.hiddenSet.has(sr.key);
                    const v = num(latest.vals[sr.key]);
                    const t = rowTotal(latest);
                    const c = (latest.carried || {})[sr.key];
                    return (
                      <Card key={sr.key}
                            role="button" tabIndex={0} aria-pressed={!off}
                            onClick={e => (e.shiftKey || e.altKey) ? isolateSeries(sr.key) : toggleSeries(sr.key)}
                            onDoubleClick={() => isolateSeries(sr.key)}
                            onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleSeries(sr.key); } }}
                            title={off ? `${sr.label} is excluded. Click to bring it back.`
                                       : `Click to exclude ${sr.label} from the chart, total and table. Double-click to show only ${sr.label}.`}
                            style={{ padding:"12px 16px", flex:"1 1 150px", minWidth:140,
                                     cursor:"pointer", userSelect:"none",
                                     opacity: off ? 0.5 : 1,
                                     borderStyle: off ? "dashed" : undefined,
                                     background: off ? "transparent" : undefined }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                          <span style={{ width:9, height:9, borderRadius:"50%", flexShrink:0,
                                         background: off ? "transparent" : sr.color,
                                         border:"1px solid "+sr.color }} />
                          <span style={{ fontSize:11, fontWeight:700, textTransform:"uppercase",
                                         letterSpacing:"0.06em", color:INK_MUTED,
                                         textDecoration: off ? "line-through" : "none" }}>{sr.label}</span>
                          {/* The only standing hint that the card is a control.
                              Without it nothing on screen says these are
                              clickable until you happen to hover one. */}
                          <span aria-hidden="true"
                                style={{ marginLeft:"auto", fontSize:13, lineHeight:1,
                                         color:INK_MUTED, opacity:0.55 }}>
                            {off ? "+" : "×"}
                          </span>
                        </div>
                        <div style={{ fontSize:18, fontWeight:600,
                                      color: (c || off) ? INK_MUTED : undefined,
                                      textDecoration: off ? "line-through" : "none",
                                      fontStyle: c ? "italic" : undefined }}>{fmt(v)}</div>
                        <div style={{ fontSize:11, marginTop:2,
                                      color: off ? B.amberDark : INK_MUTED,
                                      fontWeight: off ? 600 : undefined }}>
                          {off ? "excluded — click to restore"
                               : c ? (c.n < c.of ? `${c.n} of ${c.of} loans carried` : "carried " + fmtDate(c.from))
                               : (t ? fmtRatio(v / t) + " of total" : "—")}
                        </div>
                      </Card>
                    );
                  })}
                  <Card style={{ padding:"12px 16px", flex:"1 1 150px", minWidth:140,
                                 borderLeft:"3px solid "+B.cyan }}>
                    <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em",
                                  color:INK_MUTED, marginBottom:6 }}>
                      Total{spec.hiddenSet.size > 0 ? " (shown)" : ""}
                    </div>
                    <div style={{ fontSize:18, fontWeight:600 }}>{fmt(rowTotal(latest))}</div>
                    <div style={{ fontSize:11, color:INK_MUTED, marginTop:2 }}>
                      as of {fmtDate(latest.date)}
                      {spec.hiddenSet.size > 0 && (
                        <>
                          {" · "}
                          <span onClick={showAllSeries} role="button" tabIndex={0}
                                onKeyDown={e => { if (e.key === "Enter") showAllSeries(); }}
                                style={{ color:B.amberDark, fontWeight:600, cursor:"pointer",
                                         textDecoration:"underline" }}>
                            {spec.hiddenSet.size} excluded
                          </span>
                        </>
                      )}
                    </div>
                  </Card>
                </div>
              )}

              <Card style={{ padding:16, marginBottom:14, marginTop: spec.allSeries.length > 10 ? 14 : 0 }}>
                <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em",
                              color:B.cyanDark, marginBottom:10 }}>
                  {spec.title} <span style={{ color:INK_MUTED, fontWeight:500 }}>
                    ({spec.rows.length} period{spec.rows.length===1?"":"s"}{spec.note ? " · " + spec.note : ""}
                    {spec.carriedCount > 0 ? ` · ${spec.carriedCount} carried` : ""})
                  </span>
                </div>
                <StackedChart periods={spec.rows} series={spec.series} pct={spec.pct}
                              lineLabel={spec.lineLabel} anyCarried={spec.carriedCount}
                              allSeries={spec.allSeries} hidden={spec.hiddenSet}
                              onToggle={toggleSeries} onIsolate={isolateSeries}
                              onShowAll={showAllSeries} />
              </Card>

              <Card style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>
                    <Th>As of Date</Th>
                    {spec.series.map(sr => <Th key={sr.key} style={{ textAlign:"right" }}>{sr.label}</Th>)}
                    <Th style={{ textAlign:"right" }}>Total</Th>
                  </tr></thead>
                  <tbody>
                    {spec.rows.slice().reverse().map(r => {
                      const t = rowTotal(r);
                      return (
                        <tr key={r.key}>
                          <Td>
                            {fmtDate(r.date)}
                            {/* Nothing is carried here, so a date where only
                                some loans filed is a genuine undercount. Say so
                                rather than letting the total read as complete. */}
                            {r.expectedLoans > r.filedLoans && (
                              <span title={`${r.filedLoans} of ${r.expectedLoans} loans filed ${spec.title.toLowerCase()} on this date. Nothing is carried forward, so the loans that did not file contribute $0.00 to this row.`}
                                    style={{ fontSize:10, marginLeft:6, padding:"1px 5px", borderRadius:6,
                                             background:B.amberLight, color:B.amberDark, whiteSpace:"nowrap" }}>
                                {r.filedLoans}/{r.expectedLoans} filed
                              </span>
                            )}
                          </Td>
                          {spec.series.map(sr => {
                            const v = num(r.vals[sr.key]);
                            const c = (r.carried || {})[sr.key];
                            return (
                              <TdNum key={sr.key}
                                     style={c ? { color:INK_MUTED, fontStyle:"italic" } : undefined}
                                     title={c ? (c.n < c.of
                                            ? `${c.n} of ${c.of} loans carried forward, most recently from ${fmtDate(c.from)}`
                                            : "Carried forward from " + fmtDate(c.from) + " — not reported on this date")
                                          : undefined}>
                                {fmt(v)}
                                {c && (
                                  <span style={{ fontSize:10, marginLeft:5, padding:"1px 5px", borderRadius:6,
                                                 background:B.amberLight, color:B.amberDark, fontStyle:"normal",
                                                 whiteSpace:"nowrap" }}>
                                    {c.n < c.of ? `part carried ${c.n}/${c.of}` : "carried " + fmtDate(c.from)}
                                  </span>
                                )}
                                {spec.pct && (
                                  <span style={{ fontSize:11, color:INK_MUTED, marginLeft:6 }}>
                                    {t ? fmtRatio(v / t) : "—"}
                                  </span>
                                )}
                              </TdNum>
                            );
                          })}
                          <TdNum style={{ fontWeight:600 }}>{fmt(t)}</TdNum>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Card>
            </>}
    </>
  );
};
